/**
 * Topic spine controlled vocabulary (RFC-0005 Phase A / A1).
 *
 * The "topic spine" joins three independent sources — current canonical law,
 * WP29 drafts, and market/VOC voice — through a controlled topic vocabulary so
 * later tasks (mapper / signals / storage / feeder) can align them by a stable
 * join key instead of free text.
 *
 * Two tiers (RFC-0005 §4 appendix "主题分类法", authored/locked 2026-06-07):
 *   - Tier-1 `DomainId`: the join anchor = the 16 core `RegulatoryDomain` values
 *     (reused verbatim from primitives) ∪ 11 new experience-family domains.
 *   - Tier-2 `TopicId`: the controlled join keys, seeded from real three-source
 *     data, with a final `"unclassified"` fallback so the mapper can bucket
 *     unmatched labels for human review instead of silently dropping them.
 *
 * `defaultLens` is the topic's default `ObligationKind` (its "lens", per ADR-0007):
 * regulatory topics default to `hard_law`/`evidence_standard`, experience-family
 * and rating topics default to `market_force`. This is only a default — VOC/WP29
 * signals never become `hard_law` (red line, RFC-0005 §5 / KAIYANG §6).
 *
 * `wp29Labels` are verbatim official WP29 topic strings (source:
 * `WP29/ingestion/regpulse_gar/localization.py` EXACT_TOPIC_TRANSLATIONS) used by
 * the mapper to align WP29 drafts onto a topic. Topics with no WP29 analog (most
 * experience-family ones) carry an empty list.
 */

import { z } from "zod";

import { RegulatoryDomain, ObligationKind } from "./primitives";

/**
 * Tier-1 experience-family domains (RFC-0005 §4). VOC "white space" — areas the
 * regulatory domains do not cover; almost always seen through a `market_force`
 * lens. Independent of `RegulatoryDomain`; the two are unioned into `DomainId`.
 */
export const ExperienceDomain = z.enum([
  "cockpit_hmi",
  "connectivity_apps",
  "drivability_dynamics",
  "ride_nvh",
  "thermal_hvac",
  "interior_comfort",
  "build_quality_reliability",
  "ownership_service",
  "brand_value_residual",
  "energy_range",
  "commercial_model",
]);
export type ExperienceDomain = z.infer<typeof ExperienceDomain>;

/**
 * Tier-1 join anchor: 16 regulatory domains (authoritative legal axis) + 11
 * experience-family domains. Union, never a merged/derived enum (primitives
 * rule).
 */
export const DomainId = z.union([RegulatoryDomain, ExperienceDomain]);
export type DomainId = z.infer<typeof DomainId>;

/**
 * Tier-2 controlled join keys. The grouping comments mirror RFC-0005 §4; the
 * order is significant only in that `"unclassified"` is the locked final
 * fallback value.
 */
export const TopicId = z.enum([
  // ADAS & Automation
  "alks",
  "dcas",
  "aeb",
  "ldw_elks",
  "driver_monitoring",
  "ads_robotaxi",
  "parking_assist",
  "adas_usability",
  // General Safety (split)
  "passive_safety_crash",
  "pedestrian_safety",
  "braking",
  "steering",
  "lighting_signalling",
  "tyres",
  "vehicle_noise",
  "child_restraint",
  "door_latches",
  "ecall",
  // Emissions & CO2
  "euro7_tailpipe",
  "co2_fleet",
  "brake_particle",
  "tyre_abrasion",
  "rde_wltp",
  // Battery & Materials
  "battery_durability",
  "batteries_regulation",
  "reach_svhc",
  "elv",
  // Cybersecurity / OTA
  "csms_r155",
  "cra",
  "ad_data_storage_security",
  "sums_r156",
  "software_update_process",
  // AI
  "ai_act_high_risk",
  "gpai",
  "in_vehicle_ai",
  // Charging & Energy / Privacy / Type Approval / Rating
  "afir_interface",
  "charging_experience",
  "real_world_range",
  "vehicle_data_access",
  "eprivacy",
  "edr",
  "wvta",
  "cop",
  "iwvta",
  "deta",
  "euro_ncap",
  "insurance_rating",
  // Experience-family Tier-2
  "infotainment_responsiveness",
  "screen_ux",
  "voice_assistant",
  "cluster_layout",
  "pedal_feel",
  "one_pedal",
  "regen_tuning",
  "steering_feel",
  "ride_harshness",
  "wind_noise",
  "road_noise",
  "rattles",
  "heat_pump_experience",
  "cabin_climate",
  "preconditioning",
  // Fallback
  "unclassified",
]);
export type TopicId = z.infer<typeof TopicId>;

/**
 * A controlled-vocabulary topic record: the Tier-2 join key plus its Tier-1
 * domain anchor, default obligation lens, bilingual labels, and the source-side
 * aliases (verbatim WP29 strings + VOC keywords) used by the mapper.
 */
export const TopicRecordSchema = z.object({
  id: TopicId,
  labelEn: z.string().min(1),
  labelZh: z.string().min(1),
  domainId: DomainId,
  defaultLens: ObligationKind,
  // `.default(() => [])` (factory) so each parse gets a fresh array — a bare
  // `.default([])` shares one mutable reference across parses.
  wp29Labels: z.array(z.string()).default(() => []),
  vocKeywords: z.array(z.string()).default(() => []),
});
export type TopicRecord = z.infer<typeof TopicRecordSchema>;

/**
 * The complete controlled vocabulary: exactly one entry per `TopicId` value.
 * `wp29Labels` hold verbatim official WP29 topic strings where a topic has a
 * WP29 analog (else `[]`); `vocKeywords` are plausible EN/ZH search seeds for
 * the VOC mapper.
 */
export const TOPIC_REGISTRY: TopicRecord[] = [
  // ── ADAS & Automation ──────────────────────────────────────────────────
  {
    id: "alks",
    labelEn: "Automated Lane-Keeping Systems (ALKS)",
    labelZh: "自动车道保持系统（ALKS）",
    domainId: "ADAS & Automation",
    defaultLens: "hard_law",
    wp29Labels: ["Automated Lane-Keeping Systems (ALKS)"],
    vocKeywords: ["lane keeping", "车道保持", "ALKS", "lane centering", "车道居中", "LCC", "L3"],
  },
  {
    id: "dcas",
    labelEn: "Driver-Control Assistance Systems (DCAS)",
    labelZh: "驾驶员控制辅助系统（DCAS）",
    domainId: "ADAS & Automation",
    defaultLens: "hard_law",
    wp29Labels: ["Driver-Control Assistance Systems (DCAS)"],
    vocKeywords: [
      "driver assistance",
      "驾驶辅助",
      "DCAS",
      "L2+",
      // Cruise / pilot feature family (consumer + industry shorthand): these are
      // all L2/L2+ driver-control assistance features, so complaints and labels
      // about them home here.
      "ACC",
      "adaptive cruise",
      "自适应巡航",
      "ICA",
      "intelligent cruise",
      "智能巡航",
      "NOA",
      "navigate on autopilot",
      "领航",
      "NOP",
      "NGP",
      "traffic jam assist",
      "TJA",
      "highway assist",
      "HWA",
    ],
  },
  {
    id: "aeb",
    labelEn: "Automatic Emergency Braking (AEB)",
    labelZh: "自动紧急制动（AEB）",
    domainId: "ADAS & Automation",
    defaultLens: "hard_law",
    wp29Labels: ["Automatic Emergency Braking for M1/N1 vehicles"],
    vocKeywords: ["emergency braking", "自动紧急制动", "AEB", "FCW", "forward collision warning", "前碰撞预警"],
  },
  {
    id: "ldw_elks",
    labelEn: "Lane Departure Warning / Emergency Lane-Keeping (ELKS)",
    labelZh: "车道偏离预警 / 紧急车道保持（ELKS）",
    domainId: "ADAS & Automation",
    defaultLens: "hard_law",
    wp29Labels: [],
    vocKeywords: ["lane departure", "车道偏离", "ELKS", "lane assist", "LKA", "lane keep assist"],
  },
  {
    id: "driver_monitoring",
    labelEn: "Driver Monitoring Systems (DMS)",
    labelZh: "驾驶员监测系统（DMS）",
    domainId: "ADAS & Automation",
    defaultLens: "hard_law",
    wp29Labels: [],
    vocKeywords: ["driver monitoring", "驾驶员监测", "DMS", "drowsiness", "疲劳监测", "attention warning"],
  },
  {
    id: "ads_robotaxi",
    labelEn: "Automated Driving Systems / Robotaxi (ADS)",
    labelZh: "自动驾驶系统 / Robotaxi（ADS）",
    domainId: "ADAS & Automation",
    defaultLens: "hard_law",
    wp29Labels: ["Automated Driving Systems", "Automated Vehicle Signaling Requirements"],
    vocKeywords: ["robotaxi", "自动驾驶", "ADS", "L4"],
  },
  {
    id: "parking_assist",
    labelEn: "Parking Assistance",
    labelZh: "泊车辅助",
    domainId: "ADAS & Automation",
    defaultLens: "hard_law",
    wp29Labels: [],
    vocKeywords: [
      "parking assist",
      "自动泊车",
      "APA",
      "summon",
      "AVP",
      "automated valet parking",
      "代客泊车",
      "RPA",
      "遥控泊车",
      "memory parking",
      "记忆泊车",
      "auto park",
    ],
  },
  {
    id: "adas_usability",
    labelEn: "ADAS Usability & Experience",
    labelZh: "ADAS 易用性与体验",
    domainId: "ADAS & Automation",
    defaultLens: "market_force",
    wp29Labels: [],
    vocKeywords: [
      "adas annoying",
      "辅助驾驶体验",
      "false braking",
      "误刹",
      // ISA / speed-limiter complaint family (GSR-mandated ISA generates the
      // single most common ADAS usability complaint — give it a home so the
      // sweep finds it AND free-text labels resolve here, not unclassified).
      "intelligent speed assistance",
      "speed assistance",
      "ISA",
      "speed limiter",
      "speed limit warning",
      "限速提醒",
      "限速辅助",
      "限速",
      "speed assist",
      "phantom braking",
      "幽灵刹车",
      "blind spot",
      "盲区监测",
      "BSD",
      "BSM",
      "traffic sign recognition",
      "TSR",
      "交通标志识别",
    ],
  },
  // ── General Safety (split) ─────────────────────────────────────────────
  {
    id: "passive_safety_crash",
    labelEn: "Passive Safety & Crashworthiness",
    labelZh: "被动安全与碰撞保护",
    domainId: "General Safety",
    defaultLens: "hard_law",
    wp29Labels: ["Occupant Protection in Lateral Collisions"],
    vocKeywords: ["crash safety", "碰撞安全", "occupant protection"],
  },
  {
    id: "pedestrian_safety",
    labelEn: "Pedestrian Safety",
    labelZh: "行人安全",
    domainId: "General Safety",
    defaultLens: "hard_law",
    wp29Labels: ["Pedestrian Safety"],
    vocKeywords: ["pedestrian protection", "行人保护", "VRU"],
  },
  {
    id: "braking",
    labelEn: "Braking",
    labelZh: "制动",
    domainId: "General Safety",
    defaultLens: "hard_law",
    wp29Labels: ["Braking"],
    vocKeywords: ["braking", "制动", "brake performance"],
  },
  {
    id: "steering",
    labelEn: "Steering Equipment",
    labelZh: "转向系统",
    domainId: "General Safety",
    defaultLens: "hard_law",
    wp29Labels: ["Steering Equipment"],
    vocKeywords: ["steering", "转向系统", "steering safety"],
  },
  {
    id: "lighting_signalling",
    labelEn: "Lighting & Light-Signalling Installation",
    labelZh: "灯具及灯光信号装置安装",
    domainId: "General Safety",
    defaultLens: "hard_law",
    wp29Labels: [
      "Installation of Lighting and Lighting-Signalling Equipment",
      "Lighting",
      "Glare prevention",
      "Retro-reflective Devices and Markings (RRD)",
    ],
    vocKeywords: ["lighting", "灯光", "headlamp", "signalling"],
  },
  {
    id: "tyres",
    labelEn: "Tyres",
    labelZh: "轮胎",
    domainId: "General Safety",
    defaultLens: "hard_law",
    wp29Labels: ["Tyres"],
    vocKeywords: ["tyres", "轮胎", "tire safety"],
  },
  {
    id: "vehicle_noise",
    labelEn: "Vehicle Noise Emissions",
    labelZh: "车辆噪声排放",
    domainId: "General Safety",
    defaultLens: "hard_law",
    wp29Labels: ["Vehicle Noise Emissions"],
    vocKeywords: ["vehicle noise", "车辆噪声", "exterior noise", "AVAS"],
  },
  {
    id: "child_restraint",
    labelEn: "Child Restraint Systems",
    labelZh: "儿童约束系统",
    domainId: "General Safety",
    defaultLens: "hard_law",
    wp29Labels: ["Children Left in Vehicles"],
    vocKeywords: ["child restraint", "儿童座椅", "ISOFIX", "child seat"],
  },
  {
    id: "door_latches",
    labelEn: "Door Latches & Hinges",
    labelZh: "车门锁与铰链",
    domainId: "General Safety",
    defaultLens: "hard_law",
    wp29Labels: ["Door Latches and Door Retention Components"],
    vocKeywords: ["door latch", "车门锁", "door retention"],
  },
  {
    id: "ecall",
    labelEn: "eCall Emergency Call System",
    labelZh: "eCall 紧急呼叫系统",
    domainId: "General Safety",
    defaultLens: "hard_law",
    wp29Labels: [],
    vocKeywords: ["eCall", "紧急呼叫", "emergency call"],
  },
  // ── Emissions & CO2 ────────────────────────────────────────────────────
  {
    id: "euro7_tailpipe",
    labelEn: "Euro 7 Tailpipe Emissions",
    labelZh: "欧 7 尾气排放",
    domainId: "Emissions & CO2",
    defaultLens: "hard_law",
    wp29Labels: ["Emissions"],
    vocKeywords: ["Euro 7", "尾气排放", "tailpipe emissions", "NOx"],
  },
  {
    id: "co2_fleet",
    labelEn: "Fleet CO2 Targets",
    labelZh: "车队 CO2 目标",
    domainId: "Emissions & CO2",
    defaultLens: "hard_law",
    wp29Labels: [],
    vocKeywords: ["CO2 fleet", "车队碳排放", "fleet emissions"],
  },
  {
    id: "brake_particle",
    labelEn: "Brake Particle Emissions",
    labelZh: "制动颗粒物排放",
    domainId: "Emissions & CO2",
    defaultLens: "hard_law",
    wp29Labels: [],
    vocKeywords: ["brake particles", "制动颗粒", "brake dust", "PM"],
  },
  {
    id: "tyre_abrasion",
    labelEn: "Tyre Abrasion",
    labelZh: "轮胎磨耗",
    domainId: "Emissions & CO2",
    defaultLens: "hard_law",
    wp29Labels: ["Tyre abrasion test method development"],
    vocKeywords: ["tyre abrasion", "轮胎磨耗", "tyre wear", "microplastics"],
  },
  {
    id: "rde_wltp",
    labelEn: "Real Driving Emissions / WLTP",
    labelZh: "实际行驶排放 / WLTP",
    domainId: "Emissions & CO2",
    defaultLens: "hard_law",
    wp29Labels: [],
    vocKeywords: ["RDE", "WLTP", "实际行驶排放", "test cycle"],
  },
  // ── Battery & Materials ────────────────────────────────────────────────
  {
    id: "battery_durability",
    labelEn: "Electrified Vehicle Battery Durability",
    labelZh: "电动化车辆电池耐久性",
    domainId: "Battery & Materials",
    defaultLens: "hard_law",
    wp29Labels: ["Electrified Vehicle Battery Durability", "Electric Vehicle Safety"],
    vocKeywords: ["battery durability", "电池耐久", "state of health", "衰减"],
  },
  {
    id: "batteries_regulation",
    labelEn: "EU Batteries Regulation",
    labelZh: "欧盟电池法规",
    domainId: "Battery & Materials",
    defaultLens: "hard_law",
    wp29Labels: [],
    vocKeywords: ["batteries regulation", "电池法规", "battery passport", "电池护照"],
  },
  {
    id: "reach_svhc",
    labelEn: "REACH / SVHC Substances",
    labelZh: "REACH / 高度关注物质（SVHC）",
    domainId: "Battery & Materials",
    defaultLens: "hard_law",
    wp29Labels: [],
    vocKeywords: ["REACH", "SVHC", "高关注物质", "restricted substances"],
  },
  {
    id: "elv",
    labelEn: "End-of-Life Vehicles (ELV)",
    labelZh: "报废车辆（ELV）",
    domainId: "Battery & Materials",
    defaultLens: "hard_law",
    wp29Labels: [],
    vocKeywords: ["end of life vehicle", "报废车辆", "ELV", "recyclability"],
  },
  // ── Cybersecurity / OTA ────────────────────────────────────────────────
  {
    id: "csms_r155",
    labelEn: "Cyber Security & CSMS (UN R155)",
    labelZh: "网络安全与网络安全管理（UN R155）",
    domainId: "Cybersecurity",
    defaultLens: "hard_law",
    wp29Labels: ["Cyber Security and Cyber Security Management"],
    vocKeywords: ["cybersecurity", "网络安全", "CSMS", "R155"],
  },
  {
    id: "cra",
    labelEn: "Cyber Resilience Act (CRA)",
    labelZh: "网络韧性法案（CRA）",
    domainId: "Cybersecurity",
    defaultLens: "hard_law",
    wp29Labels: [],
    vocKeywords: ["cyber resilience act", "网络韧性", "CRA"],
  },
  {
    id: "ad_data_storage_security",
    labelEn: "Data Storage Systems for Automated Driving",
    labelZh: "自动驾驶数据存储系统",
    domainId: "Cybersecurity",
    defaultLens: "hard_law",
    wp29Labels: ["Data Storage Systems for Automated Driving"],
    vocKeywords: ["DSSAD", "自动驾驶数据存储", "data storage security"],
  },
  {
    id: "sums_r156",
    labelEn: "Software Update Management System (UN R156)",
    labelZh: "软件更新管理系统（UN R156）",
    domainId: "OTA & SUMS",
    defaultLens: "hard_law",
    wp29Labels: ["Software Updates"],
    vocKeywords: ["software updates", "软件更新", "SUMS", "R156"],
  },
  {
    id: "software_update_process",
    labelEn: "Software Update Process & OTA",
    labelZh: "软件更新流程与 OTA",
    domainId: "OTA & SUMS",
    defaultLens: "hard_law",
    wp29Labels: [],
    vocKeywords: ["OTA update", "整车 OTA", "over the air", "升级"],
  },
  // ── AI ─────────────────────────────────────────────────────────────────
  {
    id: "ai_act_high_risk",
    labelEn: "AI Act High-Risk Systems",
    labelZh: "AI 法案高风险系统",
    domainId: "AI Regulation",
    defaultLens: "hard_law",
    wp29Labels: [],
    vocKeywords: ["AI Act", "高风险 AI", "high-risk AI", "人工智能法案"],
  },
  {
    id: "gpai",
    labelEn: "General-Purpose AI (GPAI)",
    labelZh: "通用人工智能（GPAI）",
    domainId: "AI Regulation",
    defaultLens: "hard_law",
    wp29Labels: [],
    vocKeywords: ["general purpose AI", "通用人工智能", "GPAI", "foundation model"],
  },
  {
    id: "in_vehicle_ai",
    labelEn: "In-Vehicle AI",
    labelZh: "车载人工智能",
    domainId: "AI Regulation",
    defaultLens: "hard_law",
    wp29Labels: ["Artificial Intelligence"],
    vocKeywords: ["in-vehicle AI", "车载 AI", "vehicle artificial intelligence"],
  },
  // ── Charging & Energy / Privacy / Type Approval / Rating ───────────────
  {
    id: "afir_interface",
    labelEn: "AFIR Charging Interface",
    labelZh: "AFIR 充电接口",
    domainId: "Charging & Energy",
    defaultLens: "hard_law",
    wp29Labels: [],
    vocKeywords: ["AFIR", "充电接口", "charging interface", "CCS"],
  },
  {
    id: "charging_experience",
    labelEn: "Charging Experience",
    labelZh: "充电体验",
    domainId: "Charging & Energy",
    defaultLens: "market_force",
    wp29Labels: [],
    vocKeywords: [
      "charging experience",
      "充电体验",
      "charging speed",
      "充电慢",
      "fast charging",
      "快充",
      "超充",
      "V2G",
      "V2L",
      "bidirectional charging",
      "反向充电",
    ],
  },
  {
    id: "real_world_range",
    labelEn: "Real-World Range",
    labelZh: "真实续航",
    domainId: "Charging & Energy",
    defaultLens: "market_force",
    wp29Labels: ["Electric Vehicle Environmental Performance"],
    vocKeywords: ["real world range", "真实续航", "range anxiety", "续航虚标", "掉电"],
  },
  {
    id: "vehicle_data_access",
    labelEn: "Vehicle Data Access",
    labelZh: "车辆数据访问",
    domainId: "Privacy & Data",
    defaultLens: "hard_law",
    wp29Labels: [],
    vocKeywords: ["vehicle data access", "车辆数据访问", "data sharing"],
  },
  {
    id: "eprivacy",
    labelEn: "ePrivacy",
    labelZh: "电子隐私",
    domainId: "Privacy & Data",
    defaultLens: "hard_law",
    wp29Labels: [],
    vocKeywords: ["ePrivacy", "隐私", "data privacy", "GDPR"],
  },
  {
    id: "edr",
    labelEn: "Event Data Recorders (EDR)",
    labelZh: "事件数据记录仪（EDR）",
    domainId: "Privacy & Data",
    defaultLens: "hard_law",
    wp29Labels: ["Event Data Recorders"],
    vocKeywords: ["event data recorder", "事件数据记录仪", "EDR", "black box"],
  },
  {
    id: "wvta",
    labelEn: "Whole Vehicle Type Approval (WVTA)",
    labelZh: "整车型式批准（WVTA）",
    domainId: "Type Approval",
    defaultLens: "hard_law",
    wp29Labels: [],
    vocKeywords: ["whole vehicle type approval", "整车型式批准", "WVTA"],
  },
  {
    id: "cop",
    labelEn: "Conformity of Production (CoP)",
    labelZh: "生产一致性（CoP）",
    domainId: "Type Approval",
    defaultLens: "hard_law",
    wp29Labels: [],
    vocKeywords: ["conformity of production", "生产一致性", "CoP"],
  },
  {
    id: "iwvta",
    labelEn: "International Whole Vehicle Type Approval (IWVTA)",
    labelZh: "国际全车型批准体系（IWVTA）",
    domainId: "Type Approval",
    defaultLens: "hard_law",
    wp29Labels: ["International Whole Vehicle Type Approval System (IWVTA)"],
    vocKeywords: ["IWVTA", "国际全车型批准", "international type approval"],
  },
  {
    id: "deta",
    labelEn: "Type Approval Documentation Exchange (DETA)",
    labelZh: "型式批准文件交换数据库（DETA）",
    domainId: "Type Approval",
    defaultLens: "hard_law",
    wp29Labels: ["Database for the Exchange of Type Approval Documentation"],
    vocKeywords: ["DETA", "型式批准文件交换", "documentation exchange"],
  },
  {
    id: "euro_ncap",
    labelEn: "Euro NCAP Rating",
    labelZh: "Euro NCAP 评级",
    domainId: "Market Rating",
    defaultLens: "market_force",
    wp29Labels: [],
    vocKeywords: ["Euro NCAP", "安全评级", "5 star", "crash rating"],
  },
  {
    id: "insurance_rating",
    labelEn: "Insurance Rating",
    labelZh: "保险评级",
    domainId: "Market Rating",
    defaultLens: "market_force",
    wp29Labels: [],
    vocKeywords: ["insurance rating", "保险评级", "insurance group", "保费"],
  },
  // ── Experience-family Tier-2 (all market_force) ────────────────────────
  {
    id: "infotainment_responsiveness",
    labelEn: "Infotainment Responsiveness",
    labelZh: "车机响应速度",
    domainId: "cockpit_hmi",
    defaultLens: "market_force",
    wp29Labels: [],
    vocKeywords: ["infotainment lag", "车机卡顿", "screen slow", "系统卡"],
  },
  {
    id: "screen_ux",
    labelEn: "Screen UX",
    labelZh: "屏幕交互体验",
    domainId: "cockpit_hmi",
    defaultLens: "market_force",
    wp29Labels: [],
    vocKeywords: ["screen UX", "屏幕交互", "touchscreen", "menu depth"],
  },
  {
    id: "voice_assistant",
    labelEn: "Voice Assistant",
    labelZh: "语音助手",
    domainId: "cockpit_hmi",
    defaultLens: "market_force",
    wp29Labels: [],
    vocKeywords: ["voice assistant", "语音助手", "voice control", "语音识别"],
  },
  {
    id: "cluster_layout",
    labelEn: "Instrument Cluster Layout",
    labelZh: "仪表布局",
    domainId: "cockpit_hmi",
    defaultLens: "market_force",
    wp29Labels: [],
    vocKeywords: ["cluster layout", "仪表盘", "instrument display", "HUD"],
  },
  {
    id: "pedal_feel",
    labelEn: "Pedal Feel",
    labelZh: "踏板脚感",
    domainId: "drivability_dynamics",
    defaultLens: "market_force",
    wp29Labels: [],
    vocKeywords: ["pedal feel", "踏板脚感", "brake feel", "throttle response"],
  },
  {
    id: "one_pedal",
    labelEn: "One-Pedal Driving",
    labelZh: "单踏板驾驶",
    domainId: "drivability_dynamics",
    defaultLens: "market_force",
    wp29Labels: [],
    vocKeywords: ["one pedal", "单踏板", "one-pedal driving"],
  },
  {
    id: "regen_tuning",
    labelEn: "Regenerative Braking Tuning",
    labelZh: "动能回收标定",
    domainId: "drivability_dynamics",
    defaultLens: "market_force",
    wp29Labels: [],
    vocKeywords: ["regen braking", "动能回收", "regenerative tuning", "能量回收"],
  },
  {
    id: "steering_feel",
    labelEn: "Steering Feel",
    labelZh: "转向手感",
    domainId: "drivability_dynamics",
    defaultLens: "market_force",
    wp29Labels: [],
    vocKeywords: ["steering feel", "转向手感", "steering weight", "方向盘手感"],
  },
  {
    id: "ride_harshness",
    labelEn: "Ride Harshness",
    labelZh: "行驶颠簸感",
    domainId: "drivability_dynamics",
    defaultLens: "market_force",
    wp29Labels: [],
    vocKeywords: ["ride harshness", "颠簸", "ride comfort", "悬挂硬"],
  },
  {
    id: "wind_noise",
    labelEn: "Wind Noise",
    labelZh: "风噪",
    domainId: "ride_nvh",
    defaultLens: "market_force",
    wp29Labels: [],
    vocKeywords: ["wind noise", "风噪", "aero noise"],
  },
  {
    id: "road_noise",
    labelEn: "Road Noise",
    labelZh: "胎噪路噪",
    domainId: "ride_nvh",
    defaultLens: "market_force",
    wp29Labels: [],
    vocKeywords: ["road noise", "胎噪", "tyre roar", "路噪"],
  },
  {
    id: "rattles",
    labelEn: "Rattles & Squeaks",
    labelZh: "异响",
    domainId: "ride_nvh",
    defaultLens: "market_force",
    wp29Labels: [],
    vocKeywords: ["rattles", "异响", "squeaks", "BSR"],
  },
  {
    id: "heat_pump_experience",
    labelEn: "Heat Pump Experience",
    labelZh: "热泵体验",
    domainId: "thermal_hvac",
    defaultLens: "market_force",
    wp29Labels: [],
    vocKeywords: ["heat pump", "热泵", "winter range", "冬季续航"],
  },
  {
    id: "cabin_climate",
    labelEn: "Cabin Climate",
    labelZh: "座舱空调",
    domainId: "thermal_hvac",
    defaultLens: "market_force",
    wp29Labels: [],
    vocKeywords: ["cabin climate", "空调", "HVAC", "制冷制热"],
  },
  {
    id: "preconditioning",
    labelEn: "Preconditioning",
    labelZh: "预调温",
    domainId: "thermal_hvac",
    defaultLens: "market_force",
    wp29Labels: [],
    vocKeywords: ["preconditioning", "预热", "battery preconditioning", "提前空调"],
  },
  // ── Fallback ───────────────────────────────────────────────────────────
  {
    id: "unclassified",
    labelEn: "Unclassified (needs human topic mapping)",
    labelZh: "未归类（待人工归类）",
    domainId: "Ecosystem Interop",
    defaultLens: "ecosystem_signal",
    wp29Labels: [],
    vocKeywords: [],
  },
];
